/**
 * SfxVideoUpload Component
 *
 * Video upload for V2A (Video-to-Audio) providers.
 * Reuses the unified MediaUploader component.
 */

import { memo } from 'react';
import { Video } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { MediaUploader } from '../common/MediaUploader';

// ============================================================================
// Types
// ============================================================================

interface SfxVideoUploadProps {
  onVideoSelected: (file: File) => void;
  selectedFile?: File | null;
  disabled?: boolean;
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export const SfxVideoUpload = memo(function SfxVideoUpload({
  onVideoSelected,
  disabled = false,
  className = '',
}: SfxVideoUploadProps) {
  const { t } = useTranslation();

  const handleFileSelect = (file: File) => {
    onVideoSelected(file);
  };

  const handleRemove = () => {
    // Parent component should handle clearing the file
    // This is just a visual callback
  };

  return (
    <div className={`form-group ${className}`}>
      <label className="form-label-icon">
        <Video className="h-4 w-4 flex-shrink-0" />
        <span>
          {t('soundEffects.videoUpload.title', {
            defaultValue: 'Video Input (for V2A)',
          })}
        </span>
      </label>
      <p className="text-xs text-white/50 mb-3">
        {t('soundEffects.videoUpload.description', {
          defaultValue: 'Upload a video file to generate synchronized audio',
        })}
      </p>

      <MediaUploader
        accept={['video']}
        onFileSelect={handleFileSelect}
        onRemove={handleRemove}
        disabled={disabled}
        maxSizeMB={100}
        dropzoneText={t('soundEffects.videoUpload.dropzone', {
          defaultValue: 'Drag and drop your video here',
        })}
        translationNamespace="soundEffects"
      />

      {/* Info Note */}
      <div className="mt-3 p-3 rounded-lg bg-purple-900/20 border border-purple-500/30">
        <p className="text-xs text-purple-300">
          {t('soundEffects.videoUpload.info', {
            defaultValue:
              'V2A providers analyze your video and generate synchronized sound effects that match the visuals.',
          })}
        </p>
      </div>
    </div>
  );
});

export default SfxVideoUpload;
